/**
 * Privacy Policy Page
 * Displays the Uhamiaji SACCOS privacy policy, styled to match the project page layout.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Home, ChevronRight } from 'lucide-react';

const paragraphClass = 'text-gray-600 leading-relaxed';
const listClass = 'list-disc pl-5 text-gray-600 space-y-1';

const Section = ({ title, children }) => (
  <div className="mb-8 last:mb-0 border-t border-gray-100 pt-6 first:border-0 first:pt-0">
    <h3 className="text-lg md:text-xl font-bold text-[#2f3951] mb-2">{title}</h3>
    <div className="w-12 h-1 bg-[#ca9a37] mb-4" />
    {children}
  </div>
);

const PrivacyPolicyHero = () => (
  <section className="bg-[#2f3951] py-5 md:py-5">
    <div className="container mx-auto px-6 md:px-8 lg:px-12">
      {/* Breadcrumb */}
      <nav className="flex items-center text-white/80 text-sm mb-3">
        <Link to="/" className="flex items-center hover:text-[#ca9a37] transition-colors">
          <Home className="w-4 h-4 mr-1" />
          Home
        </Link>
        <ChevronRight className="w-4 h-4 mx-2" />
        <span className="text-[#ca9a37]">Privacy Policy</span>
      </nav>

      <h1 className="text-2xl md:text-4xl font-bold text-white mb-2">Privacy Policy</h1>
      <div className="w-24 h-1 bg-[#ca9a37] mb-3" />
      <p className="text-base text-gray-200 max-w-2xl">
        Last updated:{' '}
        {new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })}
      </p>
    </div>
  </section>
);

const PrivacyPolicyContent = () => (
  <section className="py-7 bg-gray-50">
    <div className="container mx-auto px-6 md:px-8 lg:px-12">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100">
        <Section title="UHAMIAJI SACCOS LIMITED PRIVACY POLICY">
          <p className={paragraphClass}>
            UHAMIAJI SAVINGS AND CREDIT COOPERATIVE SOCIETY LIMITED operates the website{' '}
            <a
              href="https://www.uhamiajisaccos.or.tz"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#ca9a37] hover:underline"
            >
              www.uhamiajisaccos.or.tz
            </a>
            , which provides financial information services. This page is used to inform website
            visitors regarding our policies with the collection, use, and disclosure of Personal
            Information if anyone decided to use our website and social media pages. If you choose
            to use our website and social media pages, then you agree to the collection and use of
            information in relation to this policy. The personal information that we collect is used
            for providing and improving our Service. We will not use or share your information with
            anyone except as described in this Privacy Policy or in compliance with laws of The United
            Republic of Tanzania.
          </p>
          <p className={`${paragraphClass} mt-3`}>
            The terms used in this Privacy Policy have the same meanings as in our Terms and
            Conditions, which is accessible on our website unless otherwise defined in this Privacy
            Policy.
          </p>
        </Section>

        <Section title="Information Collection and Use">
          <p className={paragraphClass}>
            For a better experience, while using our website, we may require you to provide us with
            certain personally identifiable information, including but not limited to your name, phone
            number, and postal address. The information that we collect will be used to contact or
            identify you.
          </p>
        </Section>

        <Section title="Log Data">
          <p className={paragraphClass}>
            We want to inform you that whenever you visit our website, we collect information that
            your browser sends to us that is called Log Data. This Log Data may include information
            such as your computer’s Internet Protocol (“IP”) address, browser version, pages of our
            Service that you visit, the time and date of your visit, the time spent on those pages,
            and other statistics.
          </p>
        </Section>

        <Section title="Cookies">
          <p className={paragraphClass}>
            Cookies are files with a small amount of data that is commonly used as an anonymous unique
            identifier. These are sent to your browser from the website that you visit and are stored
            on your computer’s hard drive. Our website uses these “cookies” to collect information and
            to improve the customer journey and user experience on our website. You have the option to
            either accept or refuse these cookies and know when a cookie is being sent to your
            computer. If you choose to refuse our cookies, you may not be able to use some portions
            of our service.
          </p>
        </Section>

        <Section title="Service Providers">
          <p className={paragraphClass}>
            We may employ third-party companies and individuals due to the following reasons:
          </p>
          <ul className={`${listClass} my-3`}>
            <li>To facilitate the operation of our website</li>
            <li>To provide the service on our behalf</li>
            <li>To perform service-related services or</li>
            <li>To assist us in analyzing the use of our service.</li>
          </ul>
          <p className={paragraphClass}>
            We want to inform our visitors that these third parties have access to your Personal
            Information. The reason is to perform the tasks assigned to them on our behalf. However,
            they are obligated not to disclose or use the information for any other purpose.
          </p>
        </Section>

        <Section title="Security">
          <p className={paragraphClass}>
            We value your trust in providing us with your personal information, thus we are striving to
            use commercially acceptable means of protecting it. But remember that no method of
            transmission over the internet, or method of electronic storage is 100% secure and
            reliable, and we cannot guarantee its absolute security.
          </p>
        </Section>

        <Section title="Links to Other Sites">
          <p className={paragraphClass}>
            Our website may contain links to other sites. If you click on a third-party link, you will
            be directed to that site. Note that these external sites are not operated by us. Therefore,
            we strongly advise you to review the Privacy Policy of these websites. We have no control
            over and assume no responsibility for the content, privacy policies, or practices of any
            third-party sites or services.
          </p>
        </Section>

        <Section title="Children’s Privacy">
          <p className={paragraphClass}>
            Our Services do not address anyone under the age of 18. We do not knowingly collect
            personally identifiable information from children under 18. In the case we discover that a
            child under 18 has provided us with personal information, we immediately delete this from
            our servers. If you are a parent or guardian and you are aware that your child has provided
            us with personal information, please contact us so that we will be able to do the necessary
            actions.
          </p>
        </Section>

        <Section title="Changes to This Privacy Policy">
          <p className={paragraphClass}>
            We may update our Privacy Policy from time to time. Thus, we advise you to review this page
            periodically for any changes. We will notify you of any changes by posting the new Privacy
            Policy on this page, please always check the most recent reviewed date. These changes are
            effective immediately after they are posted on this page.
          </p>
        </Section>

        <Section title="Contact Us">
          <p className={paragraphClass}>
            If you have any questions or suggestions about our Privacy Policy, do not hesitate to
            contact us at{' '}
            <a href="mailto:info@uhamiajisaccos.or.tz" className="text-[#ca9a37] hover:underline">
              info@uhamiajisaccos.or.tz
            </a>
          </p>
        </Section>
      </div>
    </div>
  </section>
);

const PrivacyPolicyPage = () => (
  <div className="privacy-policy-page min-h-screen bg-gray-50">
    <PrivacyPolicyHero />
    <PrivacyPolicyContent />
  </div>
);

export default PrivacyPolicyPage;
